import { Container, Graphics } from 'pixi.js';
import { ACHIEVEMENTS, ACHIEVEMENT_RULES, type AchievementDef } from '@/config/achievements';
import { COLORS } from '@/config/colors';
import { WEAPONS } from '@/config/weapons';
import { t, type I18nKey } from '@/i18n';
import { CARDS, cardName } from '@/upgrades/cards';
import { bodyText, chamferPanel, hexPath, label } from './widgets';

/** Gold for earned feats, slate for the ones still open. */
const LIT = COLORS.gemYellow;
const UNLIT = 0x3a3450;
const UNLIT_GLYPH = 0x6a6480;
const ROW_H = 50;

export function achievementDef(id: string): AchievementDef | undefined {
  return ACHIEVEMENTS.find((a) => a.id === id);
}

/** Description with its threshold filled in from ACHIEVEMENT_RULES. */
export function achievementDesc(id: string): string {
  const R = ACHIEVEMENT_RULES;
  const params: Record<string, number> = {
    m: Math.round(R.blitzSeconds / 60),
    n: id === 'arsenal' ? R.arsenalWeapons : id === 'topgun' ? R.topgunTier : R.collectorCards,
  };
  return t(`ach.${id}.desc` as I18nKey, params);
}

/** What the achievement unlocks, one line ("Start weapon: SMG"). */
export function rewardText(def: AchievementDef): string {
  return def.rewards.map((r) => {
    if (r.kind === 'endless') return t('ach.reward.endless');
    if (r.kind === 'weapon') {
      const w = WEAPONS[r.id];
      return t('ach.reward.weapon', { name: w ? t(w.nameKey as I18nKey) : r.id });
    }
    const card = CARDS.find((c) => c.id === r.id);
    return t('ach.reward.card', { name: card ? cardName(card) : r.id });
  }).join(' · ');
}

/** Hex badge with the achievement glyph, centred on (0, 0). */
export function buildBadge(def: AchievementDef, lit: boolean, r: number): Container {
  const c = new Container();
  const g = new Graphics();
  hexPath(g, 0, 0, r).fill({ color: lit ? 0x2a2014 : 0x15121f })
    .stroke({ color: lit ? LIT : UNLIT, width: Math.max(2, r * 0.12) });
  c.addChild(g);
  const glyph = label(def.glyph, Math.round(r * (def.glyph.length > 2 ? 0.62 : 0.8)),
    lit ? LIT : UNLIT_GLYPH, 0);
  glyph.anchor.set(0.5);
  c.addChild(glyph);
  return c;
}

/**
 * One achievement line: badge, name, description and reward. `w` wide,
 * top-left anchored. Unearned rows stay readable but dimmed.
 */
export function buildAchievementRow(def: AchievementDef, earned: boolean, w: number): Container {
  const c = new Container();
  const bg = new Graphics();
  chamferPanel(bg, w, ROW_H, {
    fill: earned ? 0x1d1828 : 0x120f1a,
    stroke: earned ? 0x6b5a2a : 0x2a2440,
    strokeWidth: 2, notch: 10, plain: true,
  });
  c.addChild(bg);
  const badge = buildBadge(def, earned, 17);
  badge.position.set(27, ROW_H / 2);
  c.addChild(badge);
  const name = label(t(`ach.${def.id}.name` as I18nKey), 15, earned ? LIT : 0xb8bcc6, 1);
  name.position.set(54, 7);
  c.addChild(name);
  const desc = bodyText(achievementDesc(def.id), 12, earned ? 0xd5d9de : 0x8a8f98);
  desc.anchor.set(0, 0);
  desc.position.set(54, 28);
  c.addChild(desc);
  const reward = bodyText(rewardText(def), 12, earned ? COLORS.uiAccent : 0x5d6270);
  reward.anchor.set(1, 0);
  reward.position.set(w - 12, 8);
  if (reward.width > w * 0.45) reward.scale.set((w * 0.45) / reward.width);
  c.addChild(reward);
  return c;
}

export const ACHIEVEMENT_ROW_H = ROW_H;
